/**
 * Minimal ZIP writer that keeps file contents in the browser's blob store; zip libraries hold them
 * in the JS heap and can crash the tab. Uncompressed on purpose: PDFs, DOCX and audio already are.
 */

const LOCAL_FILE_HEADER_SIGNATURE = 0x04034b50;
const CENTRAL_FILE_HEADER_SIGNATURE = 0x02014b50;
const END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
const ZIP64_END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06064b50;
const ZIP64_END_OF_CENTRAL_DIRECTORY_LOCATOR_SIGNATURE = 0x07064b50;
const ZIP64_EXTRA_FIELD_ID = 0x0001;

const LOCAL_FILE_HEADER_SIZE = 30;
const CENTRAL_FILE_HEADER_SIZE = 46;
const END_OF_CENTRAL_DIRECTORY_SIZE = 22;
const ZIP64_END_OF_CENTRAL_DIRECTORY_SIZE = 56;
const ZIP64_END_OF_CENTRAL_DIRECTORY_LOCATOR_SIZE = 20;

const STORED_COMPRESSION_METHOD = 0;
/** Bit 11: filenames and comments are UTF-8. */
const UTF8_NAME_FLAG = 0x0800;
const VERSION_STORED = 20;
const VERSION_ZIP64 = 45;

const UINT16_MAX = 0xffff;
const UINT32_MAX = 0xffffffff;

/** Read back in 8 MB slices; the CRC pass never holds more than one of these. */
const CRC_CHUNK_SIZE = 8 * 1024 * 1024;

export interface ZipEntryInput {
  /** Path inside the archive. Callers are responsible for making these unique. */
  name: string;
  blob: Blob;
}

const crcTable = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let value = i;
    for (let bit = 0; bit < 8; bit += 1) {
      value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
    }
    table[i] = value >>> 0;
  }
  return table;
})();

const updateCrc32 = (crc: number, bytes: Uint8Array): number => {
  let next = crc;
  for (let i = 0; i < bytes.length; i += 1) {
    next = crcTable[(next ^ bytes[i]) & 0xff] ^ (next >>> 8);
  }
  return next;
};

/** CRC-32 of a blob, read one slice at a time so a multi-GB file never lands in the heap. */
export const crc32OfBlob = async (blob: Blob): Promise<number> => {
  let crc = 0xffffffff;
  for (let start = 0; start < blob.size; start += CRC_CHUNK_SIZE) {
    const chunk = await blob.slice(start, start + CRC_CHUNK_SIZE).arrayBuffer();
    crc = updateCrc32(crc, new Uint8Array(chunk));
  }
  return (crc ^ 0xffffffff) >>> 0;
};

/** MS-DOS date/time pair used by the ZIP headers (2-second resolution, epoch 1980). */
const toDosDateTime = (date: Date) => ({
  time: (date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1),
  date: ((Math.max(date.getFullYear(), 1980) - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate(),
});

/**
 * Little-endian writer over a fixed-size buffer. `writeUint64` splits the value across two
 * 32-bit halves — sizes and offsets can exceed what bitwise operators handle.
 */
class ByteWriter {
  private readonly view: DataView;
  private readonly bytes: Uint8Array;
  private position = 0;

  constructor(size: number) {
    this.bytes = new Uint8Array(size);
    this.view = new DataView(this.bytes.buffer);
  }

  writeUint16(value: number) {
    this.view.setUint16(this.position, value, true);
    this.position += 2;
  }

  writeUint32(value: number) {
    this.view.setUint32(this.position, value >>> 0, true);
    this.position += 4;
  }

  writeUint64(value: number) {
    this.view.setUint32(this.position, value % 0x100000000, true);
    this.view.setUint32(this.position + 4, Math.floor(value / 0x100000000), true);
    this.position += 8;
  }

  writeBytes(value: Uint8Array) {
    this.bytes.set(value, this.position);
    this.position += value.length;
  }

  result(): Uint8Array {
    return this.bytes;
  }
}

interface EntryRecord {
  nameBytes: Uint8Array;
  crc: number;
  size: number;
  /** Offset of this entry's local file header from the start of the archive. */
  localHeaderOffset: number;
  time: number;
  date: number;
}

const buildLocalFileHeader = ({ nameBytes, crc, size, time, date }: Omit<EntryRecord, 'localHeaderOffset'>) => {
  // Entries ≥4 GB need a ZIP64 extra field.
  const needsZip64 = size >= UINT32_MAX;
  const extraSize = needsZip64 ? 20 : 0;
  const writer = new ByteWriter(LOCAL_FILE_HEADER_SIZE + nameBytes.length + extraSize);

  writer.writeUint32(LOCAL_FILE_HEADER_SIGNATURE);
  writer.writeUint16(needsZip64 ? VERSION_ZIP64 : VERSION_STORED);
  writer.writeUint16(UTF8_NAME_FLAG);
  writer.writeUint16(STORED_COMPRESSION_METHOD);
  writer.writeUint16(time);
  writer.writeUint16(date);
  writer.writeUint32(crc);
  writer.writeUint32(needsZip64 ? UINT32_MAX : size);
  writer.writeUint32(needsZip64 ? UINT32_MAX : size);
  writer.writeUint16(nameBytes.length);
  writer.writeUint16(extraSize);
  writer.writeBytes(nameBytes);

  if (needsZip64) {
    writer.writeUint16(ZIP64_EXTRA_FIELD_ID);
    writer.writeUint16(16);
    writer.writeUint64(size); // uncompressed
    writer.writeUint64(size); // compressed — identical, entries are stored
  }

  return writer.result();
};

const buildCentralFileHeader = ({ nameBytes, crc, size, localHeaderOffset, time, date }: EntryRecord) => {
  const sizeNeedsZip64 = size >= UINT32_MAX;
  const offsetNeedsZip64 = localHeaderOffset >= UINT32_MAX;
  const needsZip64 = sizeNeedsZip64 || offsetNeedsZip64;
  // The ZIP64 extra holds exactly the fields that overflowed, in a fixed order.
  const extraDataSize = (sizeNeedsZip64 ? 16 : 0) + (offsetNeedsZip64 ? 8 : 0);
  const extraSize = needsZip64 ? extraDataSize + 4 : 0;
  const writer = new ByteWriter(CENTRAL_FILE_HEADER_SIZE + nameBytes.length + extraSize);

  writer.writeUint32(CENTRAL_FILE_HEADER_SIGNATURE);
  writer.writeUint16(needsZip64 ? VERSION_ZIP64 : VERSION_STORED); // version made by
  writer.writeUint16(needsZip64 ? VERSION_ZIP64 : VERSION_STORED); // version needed
  writer.writeUint16(UTF8_NAME_FLAG);
  writer.writeUint16(STORED_COMPRESSION_METHOD);
  writer.writeUint16(time);
  writer.writeUint16(date);
  writer.writeUint32(crc);
  writer.writeUint32(sizeNeedsZip64 ? UINT32_MAX : size);
  writer.writeUint32(sizeNeedsZip64 ? UINT32_MAX : size);
  writer.writeUint16(nameBytes.length);
  writer.writeUint16(extraSize);
  writer.writeUint16(0); // comment length
  writer.writeUint16(0); // disk number start
  writer.writeUint16(0); // internal attributes
  writer.writeUint32(0); // external attributes
  writer.writeUint32(offsetNeedsZip64 ? UINT32_MAX : localHeaderOffset);
  writer.writeBytes(nameBytes);

  if (needsZip64) {
    writer.writeUint16(ZIP64_EXTRA_FIELD_ID);
    writer.writeUint16(extraDataSize);
    if (sizeNeedsZip64) {
      writer.writeUint64(size); // uncompressed
      writer.writeUint64(size); // compressed
    }
    if (offsetNeedsZip64) {
      writer.writeUint64(localHeaderOffset);
    }
  }

  return writer.result();
};

const buildZip64EndRecords = (entryCount: number, centralDirectorySize: number, centralDirectoryOffset: number) => {
  const writer = new ByteWriter(ZIP64_END_OF_CENTRAL_DIRECTORY_SIZE + ZIP64_END_OF_CENTRAL_DIRECTORY_LOCATOR_SIZE);

  writer.writeUint32(ZIP64_END_OF_CENTRAL_DIRECTORY_SIGNATURE);
  writer.writeUint64(ZIP64_END_OF_CENTRAL_DIRECTORY_SIZE - 12); // size of the rest of this record
  writer.writeUint16(VERSION_ZIP64); // version made by
  writer.writeUint16(VERSION_ZIP64); // version needed
  writer.writeUint32(0); // this disk
  writer.writeUint32(0); // disk holding the central directory
  writer.writeUint64(entryCount); // entries on this disk
  writer.writeUint64(entryCount); // entries in total
  writer.writeUint64(centralDirectorySize);
  writer.writeUint64(centralDirectoryOffset);

  writer.writeUint32(ZIP64_END_OF_CENTRAL_DIRECTORY_LOCATOR_SIGNATURE);
  writer.writeUint32(0); // disk holding the ZIP64 end record
  writer.writeUint64(centralDirectoryOffset + centralDirectorySize); // where that record starts
  writer.writeUint32(1); // total disks

  return writer.result();
};

const buildEndOfCentralDirectory = (
  entryCount: number,
  centralDirectorySize: number,
  centralDirectoryOffset: number,
  hasZip64Records: boolean,
) => {
  const writer = new ByteWriter(END_OF_CENTRAL_DIRECTORY_SIZE);

  writer.writeUint32(END_OF_CENTRAL_DIRECTORY_SIGNATURE);
  writer.writeUint16(0); // this disk
  writer.writeUint16(0); // disk holding the central directory
  // With ZIP64 in play these stay pinned at the marker values and readers use the ZIP64 record.
  writer.writeUint16(hasZip64Records ? UINT16_MAX : entryCount);
  writer.writeUint16(hasZip64Records ? UINT16_MAX : entryCount);
  writer.writeUint32(hasZip64Records ? UINT32_MAX : centralDirectorySize);
  writer.writeUint32(hasZip64Records ? UINT32_MAX : centralDirectoryOffset);
  writer.writeUint16(0); // comment length

  return writer.result();
};

/** Packs `entries` into an uncompressed ZIP. The result references the input blobs instead of copying them. */
export const createZipBlob = async (entries: ZipEntryInput[], now: Date = new Date()): Promise<Blob> => {
  const encoder = new TextEncoder();
  const { time, date } = toDosDateTime(now);
  const parts: BlobPart[] = [];
  const records: EntryRecord[] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = encoder.encode(entry.name);
    const crc = await crc32OfBlob(entry.blob);
    const record: EntryRecord = {
      nameBytes,
      crc,
      size: entry.blob.size,
      localHeaderOffset: offset,
      time,
      date,
    };
    const localHeader = buildLocalFileHeader(record);

    parts.push(localHeader, entry.blob);
    records.push(record);
    offset += localHeader.length + entry.blob.size;
  }

  const centralDirectoryOffset = offset;
  let centralDirectorySize = 0;
  for (const record of records) {
    const centralHeader = buildCentralFileHeader(record);
    parts.push(centralHeader);
    centralDirectorySize += centralHeader.length;
  }

  const needsZip64 =
    records.length > UINT16_MAX ||
    centralDirectorySize >= UINT32_MAX ||
    centralDirectoryOffset >= UINT32_MAX ||
    records.some((record) => record.size >= UINT32_MAX);

  if (needsZip64) {
    parts.push(buildZip64EndRecords(records.length, centralDirectorySize, centralDirectoryOffset));
  }
  parts.push(buildEndOfCentralDirectory(records.length, centralDirectorySize, centralDirectoryOffset, needsZip64));

  return new Blob(parts, { type: 'application/zip' });
};
